import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, m, useAnimationControls, useReducedMotion } from 'framer-motion';
import { shake, spring, timing } from '../motion.js';
import { uid } from '../../../utils/mlb-the-show/format.js';
import { Count, DrawnCheck } from './primitives.js';

export function useShake() {
  const controls = useAnimationControls();
  const reduce = useReducedMotion();
  const run = useCallback(() => {
    if (!reduce) controls.start(shake);
  }, [controls, reduce]);
  return [controls, run];
}

export function SaveButton({ onSave, saved }) {
  const [controls, runShake] = useShake();
  return (
    <m.button
      type="button"
      className="st-save"
      animate={controls}
      whileTap={{ scale: 0.985 }}
      onClick={() => {
        if (!onSave()) runShake();
      }}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {saved ? (
          <m.span key="saved" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
            <DrawnCheck /> Saved
          </m.span>
        ) : (
          <m.span key="save" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
            Save game
          </m.span>
        )}
      </AnimatePresence>
    </m.button>
  );
}

/** Away share fills from the left; home is the track. */
export function WinBar({ homeName, awayName, homeChance }) {
  const home = Math.round(homeChance * 100);
  return (
    <div className="st-wp">
      <div className="st-wp-labels">
        <span>
          <b>{awayName}</b> <Count value={100 - home} />%
        </span>
        <span>Win chance</span>
        <span>
          <Count value={home} />% <b>{homeName}</b>
        </span>
      </div>
      <div className="st-wp-track">
        <m.i className="st-wp-fill" initial={false} animate={{ scaleX: 1 - homeChance }} transition={spring.standard} />
      </div>
    </div>
  );
}

export function useUndo() {
  const [toast, setToast] = useState(null);
  const current = useRef(null);
  current.current = toast;
  const show = useCallback((message, restore) => setToast({ id: uid(), message, restore }), []);
  const undo = useCallback(() => {
    current.current?.restore();
    current.current = null;
    setToast(null);
  }, []);
  const dismiss = useCallback((id) => setToast((t) => (t && t.id === id ? null : t)), []);
  return { toast, show, undo, dismiss };
}

function ToastBody({ toast, onUndo, onDone }) {
  // A timer (not the bar's animation) closes the toast, so reduced motion can't shorten the window.
  useEffect(() => {
    const t = setTimeout(() => onDone(toast.id), timing.undoMs);
    return () => clearTimeout(t);
  }, [toast.id, onDone]);
  return (
    <>
      <span>{toast.message}</span>
      <button type="button" onClick={onUndo}>Undo</button>
      <m.i
        className="st-toast-bar"
        initial={{ scaleX: 1 }}
        animate={{ scaleX: 0 }}
        transition={{ duration: timing.undoMs / 1000, ease: 'linear' }}
      />
    </>
  );
}

export function UndoToast({ state }) {
  return (
    <div className="st-toast-wrap">
      <AnimatePresence>
        {state.toast && (
          <m.div
            key={state.toast.id}
            className="st-toast"
            role="status"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={spring.standard}
          >
            <ToastBody toast={state.toast} onUndo={state.undo} onDone={state.dismiss} />
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Empty({ children }) {
  return <div className="st-empty">{children}</div>;
}
